/* =====================================================================
   AstaLab 2.0 — importLegacyBackup() (obiettivo 9)

   Importa un backup JSON AstaLab 1.3 (lo stesso file scaricabile dalla
   1.3.0-beta.6 con "Crea backup") dentro una NUOVA asta cloud/demo,
   tramite AuctionRepository — mai scrivendo direttamente su un adapter.

   Cosa fa questo modulo, esattamente:
   - valida la STRUTTURA minima del backup (stesso cancello che usa
     importJSONBackup() nella 1.3: `players` deve essere un array — vedi
     astalab.html, funzione importJSONBackup);
   - NON reimplementa l'intera pipeline di normalizzazione/riconciliazione
     della 1.3 (normalizePlayer/prepareExternalState/reconcileAllExternalRecords
     e affini): sarebbe una duplicazione rischiosa di logica già
     complessa e verificata altrove, fuori scopo per le "fondamenta"
     di questo round (vedi ARCHITECTURE_2.md, "limiti aperti");
   - conserva il backup così com'è, DENTRO un involucro versionato
     esplicito {schemaOrigin, sourceDataVersion, importedAt, raw}, mai
     riscritto o "aggiornato" silenziosamente a uno schema 2.0 ancora
     inesistente;
   - crea SEMPRE una asta nuova (mai sovrascrive un'asta esistente: non
     distruttivo per costruzione, non richiede nemmeno un parametro
     "asta di destinazione").

   Transazionalità (limite onesto dell'alpha.1): in assenza di una
   funzione lato database che unisca "crea asta" + "salva stato" in una
   singola transazione reale (prevista per alpha.2, vedi ARCHITECTURE_2.md),
   questo modulo simula l'atomicità con un rollback compensativo lato
   applicazione — se il salvataggio dello stato fallisce dopo che l'asta
   è già stata creata, l'asta appena creata viene immediatamente
   archiviata (mai lasciata "attiva ma vuota") e l'errore viene
   restituito con chiarezza. Non è una vera transazione SQL: è
   documentato come tale, non spacciato per altro.
   ===================================================================== */

import { logClientError } from "../utils/clientLogger.js";

const CURRENT_LEGACY_SCHEMA_ORIGIN = "astalab-1.3";

/**
 * @param {unknown} parsedBackup - risultato di JSON.parse() su un file di backup 1.3
 * @returns {{ok:true} | {ok:false, error:string}}
 */
export function validateLegacyBackupShape(parsedBackup){
  if(parsedBackup == null || typeof parsedBackup !== "object"){
    return { ok:false, error:"Il file non è un backup JSON valido." };
  }
  if(!Array.isArray(parsedBackup.players)){
    return { ok:false, error:'Struttura del backup non riconosciuta: manca (o non è un array) il campo "players".' };
  }
  if(parsedBackup.config != null && typeof parsedBackup.config !== "object"){
    return { ok:false, error:'Il campo "config" del backup, se presente, deve essere un oggetto.' };
  }
  return { ok:true };
}

/**
 * @param {import("../repository/AuctionRepository.js").AuctionRepository} repository
 * @param {unknown} parsedBackup
 * @param {{name?:string, season?:string|null}} [options]
 * @returns {Promise<{ok:boolean, auction:Object|null, error:string|null}>}
 */
export async function importLegacyBackup(repository, parsedBackup, options){
  const shape = validateLegacyBackupShape(parsedBackup);
  if(!shape.ok) return { ok:false, auction:null, error: shape.error };

  const opts = options || {};
  const name = String(opts.name || (parsedBackup.config && parsedBackup.config.nomeLega) || "Asta importata da AstaLab 1.3").trim();
  const season = opts.season || null;

  const created = await repository.createAuction({ name, season });
  if(!created.ok){
    return { ok:false, auction:null, error: created.error || "Creazione dell'asta fallita: nessun dato importato." };
  }

  const envelope = {
    schemaOrigin: CURRENT_LEGACY_SCHEMA_ORIGIN,
    sourceDataVersion: (parsedBackup && typeof parsedBackup.version !== "undefined") ? parsedBackup.version : null,
    importedAt: new Date().toISOString(),
    raw: parsedBackup
  };

  const saved = await repository.saveAuction(created.auction.id, envelope, 0);
  if(!saved.ok){
    // Rollback compensativo (vedi commento in cima al file): l'asta appena creata non deve mai
    // restare visibile come "attiva" se il suo stato non è mai stato salvato con successo — ma
    // l'esito REALE di questo stesso tentativo di rollback (round Beta-P3-6, design D, accettato:
    // sostituisce il precedente `.catch(()=>{})` cieco, che collassava silenziosamente sia un
    // rigetto sia un {ok:false} risolto nello stesso, unico messaggio che affermava SEMPRE
    // un'archiviazione riuscita) determina ora quale dei due messaggi sotto viene restituito — mai
    // un'affermazione automatica di successo non verificata.
    let archiveOk = false;
    try{
      const archiveResult = await repository.archiveAuction(created.auction.id);
      archiveOk = !!(archiveResult && archiveResult.ok);
      if(!archiveOk){
        // Risolto ma {ok:false}: stessa classe di fallimento di compensazione di un rigetto sotto —
        // stesso log diagnostico, mai il contenuto di archiveResult.error esposto qui o all'utente
        // (il logger strutturale accettato da Beta-P3-5 non legge comunque alcuna proprietà
        // dell'errore che gli viene passato — vedi utils/clientLogger.js).
        logClientError({ area:"legacy-import", operation:"rollback-archive", error: archiveResult && archiveResult.error, context:{ auctionId: created.auction.id } });
      }
    }catch(err){
      archiveOk = false;
      logClientError({ area:"legacy-import", operation:"rollback-archive", error:err, context:{ auctionId: created.auction.id } });
    }

    const primaryReason = "Import non riuscito durante il salvataggio dello stato: " + (saved.error || "errore sconosciuto");
    return {
      ok:false, auction:null,
      error: archiveOk
        ? primaryReason + " (l'asta creata è stata archiviata automaticamente, nessun dato parziale resta attivo)."
        : primaryReason + " L'asta appena creata NON è stata archiviata automaticamente e resta visibile e attiva " +
          "nel tuo elenco aste. Puoi archiviarla manualmente da lì. Prima di riprovare, gestisci questa asta: un " +
          "nuovo tentativo può creare un'altra asta separata."
    };
  }

  return { ok:true, auction: created.auction, error:null };
}
